import { useMemo, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Stack,
  Tabs,
  Tab,
  Tooltip,
  useTheme,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import InfoIcon from "@mui/icons-material/Info";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { Link as RouterLink } from "react-router-dom";
import { tokens } from "../../../theme";
import { ApiEvaluation, ApplicationStatus } from "../StreamFundApplication";
import StageEvaluationCard from "./StageEvaluationCard";

const OPERATIONS_CRITERIA: Record<string, string> = {
  venue_infrastructure_readiness: "Venue and Infrastructure Readiness",
  crew_equipment_availability: "Crew and Equipment Availability",
  logistics_feasibility: "Logistics Feasibility",
  schedule_conflict_risk: "Schedule Conflict Risk",
};

const FINANCE_CRITERIA: Record<string, string> = {
  production_cost_estimate: "Production Cost Estimate",
  sponsorship_marketing: "Sponsorship and Marketing",
  budget_alignment: "Budget Alignment",
  promotional_value: "Promotional Value",
};

const ADMIN_CRITERIA: Record<string, string> = {
  strategic_impact: "Strategic Impact",
  technical_readiness: "Technical Readiness",
  execution_capability: "Execution Capability",
  timeline_feasibility: "Timeline Feasibility",
};

type Stage = "operations" | "finance" | "admin";

interface EvaluationPanelProps {
  referenceId: string;
  conferenceDate: string;
  status: ApplicationStatus;
  onStatusChange: (value: ApplicationStatus) => void;
  onConvert: () => void;
  convertedBooking?: { id: number; reference_number: string | null } | null;
  isAdmin?: boolean;
  isOperations?: boolean;
  isFinance?: boolean;
  evaluations: ApiEvaluation[];
  onSaveEvaluation: (
    stage: Stage,
    payload: {
      scores: Record<string, number>;
      comments: string;
      risks: string;
    },
  ) => Promise<ApiEvaluation | undefined>;
}

export default function EvaluationPanel({
  referenceId,
  conferenceDate,
  status,
  onStatusChange,
  onConvert,
  convertedBooking = null,
  isAdmin = false,
  isOperations = false,
  isFinance = false,
  evaluations,
  onSaveEvaluation,
}: EvaluationPanelProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const cardBg = colors.primary[900];
  const textPrimary = colors.primary[700];
  const textMuted = colors.primary[300];

  const operationsEvaluation =
    evaluations.find((e) => e.stage === "operations") ?? null;
  const financeEvaluation =
    evaluations.find((e) => e.stage === "finance") ?? null;
  const adminEvaluation =
    evaluations.find((e) => e.stage === "admin") ?? null;

  const operationsComplete = operationsEvaluation?.is_complete ?? false;
  const financeComplete = financeEvaluation?.is_complete ?? false;

  const financeLocked = !operationsComplete;
  const adminLocked = !financeComplete;

  const stageMeta: Record<
    Stage,
    { canEdit: boolean; locked: boolean; complete: boolean }
  > = {
    operations: {
      canEdit: isOperations,
      locked: false,
      complete: operationsComplete,
    },
    finance: {
      canEdit: isFinance,
      locked: financeLocked,
      complete: financeComplete,
    },
    admin: {
      canEdit: isAdmin,
      locked: adminLocked,
      complete: adminEvaluation?.is_complete ?? false,
    },
  };

  const defaultStage = useMemo<Stage>(() => {
    const order: Stage[] = ["operations", "finance", "admin"];
    const mine = order.find(
      (s) => stageMeta[s].canEdit && !stageMeta[s].locked && !stageMeta[s].complete,
    );
    if (mine) return mine;
    const anyOpen = order.find((s) => !stageMeta[s].locked && !stageMeta[s].complete);
    return anyOpen ?? "operations";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [activeStage, setActiveStage] = useState<Stage>(defaultStage);

  const stageLabels: Record<Stage, string> = {
    operations: "Operations",
    finance: "Finance",
    admin: "Admin",
  };

  const getDaysLeft = (): number | null => {
    if (!conferenceDate) return null;
    const eventDate = new Date(conferenceDate);
    if (isNaN(eventDate.getTime())) return null;

    const eventUTC = Date.UTC(
      eventDate.getUTCFullYear(),
      eventDate.getUTCMonth(),
      eventDate.getUTCDate(),
    );
    const today = new Date();
    const todayUTC = Date.UTC(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );

    const diffMs = eventUTC - todayUTC;
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  };

  const daysLeft = getDaysLeft();
  const showCritical = daysLeft !== null && daysLeft <= 5;

  const getWarningText = (days: number): string => {
    if (days < 0) return "Critical: Event Passed";
    if (days === 0) return "Critical: Today";
    if (days === 1) return "Critical: 1 Day Left";
    return `Critical: ${days} Days Left`;
  };

  // Overall score across every criterion rated so far, across all stages.
  const ratedValues = evaluations
    .flatMap((e) => Object.values(e.scores ?? {}))
    .filter((v) => typeof v === "number");
  const overallScore = ratedValues.length
    ? (ratedValues.reduce((sum, v) => sum + v, 0) / ratedValues.length).toFixed(
        1,
      )
    : "0.0";

  const warningBg = isDark ? "#F79E1B22" : "#FFF8E1";
  const warningColor = isDark ? "#F79E1B" : "#B78103";
  const overallBg = isDark ? "#3B82F622" : "#EBF3FF";
  const overallColor = isDark ? "#60A5FA" : "#1D4ED8";

  const stageCriteria: Record<Stage, Record<string, string>> = {
    operations: OPERATIONS_CRITERIA,
    finance: FINANCE_CRITERIA,
    admin: ADMIN_CRITERIA,
  };
  const stageEvaluation: Record<Stage, ApiEvaluation | null> = {
    operations: operationsEvaluation,
    finance: financeEvaluation,
    admin: adminEvaluation,
  };
  const stageCanEdit: Record<Stage, boolean> = {
    operations: isOperations,
    finance: isFinance,
    admin: isAdmin,
  };
  const stageLockedReason: Record<Stage, string | undefined> = {
    operations: undefined,
    finance: "Locked until the Operations evaluation is complete.",
    admin: "Locked until the Finance evaluation is complete.",
  };

  return (
    <Card elevation={0} sx={{ bgcolor: cardBg, borderRadius: "18px", p: 2.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-end" mb={2} flexWrap="wrap" rowGap={1}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
          <Typography sx={{ fontSize: 34, fontWeight: 800, color: overallColor, lineHeight: 1 }}>
            {overallScore}
          </Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: textMuted }}>
            / 5.0
          </Typography>
        </Box>
        {showCritical && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              bgcolor: warningBg,
              color: warningColor,
              px: 1.5,
              py: 0.6,
              borderRadius: "20px",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 14 }} />
            {getWarningText(daysLeft!)}
          </Box>
        )}
      </Stack>

      <Tabs
        value={activeStage}
        onChange={(_, val) => setActiveStage(val)}
        variant="fullWidth"
        sx={{
          minHeight: 40,
          mb: 2,
          borderRadius: "12px",
          bgcolor: `${textMuted}0c`,
          "& .MuiTabs-indicator": { bgcolor: "#3B82F6", height: 2.5 },
        }}
      >
        {(["operations", "finance", "admin"] as Stage[]).map((stage) => (
          <Tab
            key={stage}
            value={stage}
            label={stageLabels[stage]}
            iconPosition="start"
            icon={
              stageMeta[stage].locked ? (
                <LockOutlinedIcon sx={{ fontSize: 15 }} />
              ) : stageMeta[stage].complete ? (
                <CheckCircleIcon sx={{ fontSize: 15, color: "#4BA663" }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ fontSize: 15 }} />
              )
            }
            sx={{
              minHeight: 40,
              textTransform: "none",
              fontSize: 12.5,
              fontWeight: 700,
              color: textMuted,
              "&.Mui-selected": { color: textPrimary },
            }}
          />
        ))}
      </Tabs>

      <Box sx={{ mb: 3 }}>
        {(["operations", "finance", "admin"] as Stage[]).map((stage) => (
          <Box key={stage} sx={{ display: activeStage === stage ? "block" : "none" }}>
            <StageEvaluationCard
              criteria={stageCriteria[stage]}
              evaluation={stageEvaluation[stage]}
              canEdit={stageCanEdit[stage]}
              locked={stageMeta[stage].locked}
              lockedReason={stageLockedReason[stage]}
              onSave={(payload) => onSaveEvaluation(stage, payload)}
            />
          </Box>
        ))}
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 800,
            color: textPrimary,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            mb: 1.5,
          }}
        >
          Decision Center
        </Typography>

        {convertedBooking ? (
          <Button
            component={RouterLink}
            to={`/bookings/${convertedBooking.id}`}
            variant="contained"
            fullWidth
            sx={{
              bgcolor: "#4BA663",
              color: "#FFFFFF",
              textTransform: "none",
              borderRadius: "10px",
              py: 1.25,
              fontWeight: 700,
              fontSize: 13,
              boxShadow: "none",
              "&:hover": { bgcolor: "#3E8C52" },
            }}
          >
            Converted → Booking{" "}
            {convertedBooking.reference_number ?? `#${convertedBooking.id}`}
          </Button>
        ) : status === "approved" ? (
          <Button
            variant="contained"
            fullWidth
            onClick={onConvert}
            sx={{
              bgcolor: "#3B82F6",
              color: "#FFFFFF",
              textTransform: "none",
              borderRadius: "10px",
              py: 1.25,
              fontWeight: 700,
              fontSize: 13,
              boxShadow: "none",
              "&:hover": { bgcolor: "#2563EB" },
            }}
          >
            Convert to Booking
          </Button>
        ) : (
          <Stack spacing={1.25}>
            {isAdmin && (
              <Tooltip
                title={
                  stageMeta.admin.complete
                    ? ""
                    : "Complete the Admin evaluation before approving."
                }
              >
                <span>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => onStatusChange("approved")}
                    disabled={!stageMeta.admin.complete}
                    startIcon={<CheckCircleIcon />}
                    sx={{
                      bgcolor: "#4BA663",
                      color: "#FFFFFF",
                      textTransform: "none",
                      borderRadius: "10px",
                      py: 1.1,
                      fontWeight: 700,
                      fontSize: 13,
                      boxShadow: "none",
                      "&:hover": { bgcolor: "#3E8A52" },
                      "&.Mui-disabled": {
                        bgcolor: `${textMuted}22`,
                        color: textMuted,
                      },
                    }}
                  >
                    Approve
                  </Button>
                </span>
              </Tooltip>
            )}

            <Button
              variant="contained"
              fullWidth
              onClick={() => onStatusChange("under_review")}
              startIcon={<InfoIcon />}
              sx={{
                bgcolor: "#F79E1B",
                color: "#FFFFFF",
                textTransform: "none",
                borderRadius: "10px",
                py: 1.1,
                fontWeight: 700,
                fontSize: 13,
                boxShadow: "none",
                "&:hover": { bgcolor: "#D9820F" },
              }}
            >
              Request Info
            </Button>

            {isAdmin && (
              <Button
                variant="contained"
                fullWidth
                onClick={() => onStatusChange("rejected")}
                startIcon={<RemoveCircleIcon />}
                sx={{
                  bgcolor: "#EB4335",
                  color: "#FFFFFF",
                  textTransform: "none",
                  borderRadius: "10px",
                  py: 1.1,
                  fontWeight: 700,
                  fontSize: 13,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#C5352A" },
                }}
              >
                Decline
              </Button>
            )}
          </Stack>
        )}
      </Box>
    </Card>
  );
}